import qrcode from "qrcode-generator";
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { getPayOnline, payOnlineStatusText, type PayOnlineView } from "../../api/subscriberRecharge";

/** How often the waiting dialog asks whether the link is paid. */
const POLL_MS = 5000;

function qrDataURL(text: string): string {
  const qr = qrcode(0, "M");
  qr.addData(text);
  qr.make();
  return qr.createDataURL(5, 2);
}

/**
 * The Pay online waiting state (owner 2026-10-28): the link's QR for the
 * subscriber to scan, the link itself, and the answer. While it is open,
 * radaccess applies the recharge the moment the gateway says paid; once it
 * is closed, radaccess's background sweep does (owner POL-3).
 */
export function PayOnlinePanel({ ns, accountId, started }: { ns: string; accountId: string; started: PayOnlineView }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const live = useQuery({
    queryKey: ["payonline", ns, accountId, started.orderid],
    queryFn: ({ signal }) => getPayOnline(identity, ns, accountId, started.orderid, signal),
    initialData: started,
    refetchInterval: (q) => (q.state.data?.status === "Pending" ? POLL_MS : false),
    retry: false,
  });
  const v = live.data ?? started;
  const applied = v.status === "Applied";
  useEffect(() => {
    if (!applied) return;
    void qc.invalidateQueries({ queryKey: ["subscriberreceipts", ns, accountId] });
    void qc.invalidateQueries({ queryKey: ["rechargeoptions", ns, accountId] });
  }, [applied, qc, ns, accountId]);

  const expires = new Date(v.expiresat).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {v.status === "Pending" && v.checkouturl && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <img src={qrDataURL(v.checkouturl)} alt="Payment QR" width={180} height={180} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2">Ask the subscriber to scan this and pay ₹{v.amount}.</Typography>
            {started.sms && (
              <Typography variant="body2" color="text.secondary">
                The gateway is also texting the link to their mobile.
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: "break-all" }}>
              <Link href={v.checkouturl} target="_blank" rel="noopener noreferrer">
                {v.checkouturl}
              </Link>
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Payable until {expires} IST.
            </Typography>
          </Box>
        </Box>
      )}
      <Alert severity={applied ? "success" : v.status === "Pending" ? "info" : "warning"} icon={v.status === "Pending" ? <CircularProgress size={18} /> : undefined}>
        {payOnlineStatusText(v)}
      </Alert>
      {v.status === "Pending" && (
        <Typography variant="caption" color="text.secondary">
          You can close this: the recharge is applied by itself once the gateway says paid (checked every minute, then every 30 minutes, while the link is valid).
        </Typography>
      )}
      {live.error && <Alert severity="error">{live.error instanceof Error ? live.error.message : String(live.error)}</Alert>}
    </Box>
  );
}
